import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Widget from '../Widget/Widget';
import { Row, Col, Button } from 'reactstrap';
import Icon from '@mdi/react';
import { mdiChevronDown, mdiChevronUp } from '@mdi/js';

import './TransactionsResume.css';

const completed = (t) => t.completed;
const noCustom = (t) => !t.custom_acq_contract && !t.custom_psp_contract;

function groupTransactionsByKey(transactions, keyFn) {
    const map = new Map();
    for (const t of transactions) {
        const key = keyFn(t);
        if (key === null)
            continue;
        if (!map.has(key))
            map.set(key, []);
        map.get(key).push(t);
    }
    return map;
}

/**
 * Rows for custom_acq_contract (one per acquirer name), then custom_psp only (one per PSP name).
 */
function buildCustomPartnerRows(transactions) {
    const acqMap = groupTransactionsByKey(
        transactions,
        (t) => (t.custom_acq_contract ? ((t.acquirer && t.acquirer.name) || '—') : null)
    );
    const pspMap = groupTransactionsByKey(
        transactions,
        (t) => (
            t.custom_psp_contract && !t.custom_acq_contract
                ? ((t.payment_system && t.payment_system.name) || '—')
                : null
        )
    );

    const rows = [];
    for (const [partner, txs] of acqMap.entries()) {
        rows.push({ key: `acq-${partner}`, partner, txs });
    }
    for (const [partner, txs] of pspMap.entries()) {
        rows.push({ key: `psp-${partner}`, partner, txs });
    }
    rows.sort((a, b) => a.partner.localeCompare(b.partner, 'fr'));
    return rows;
}

function aggregateFourMetrics(txs) {
    const list = txs || [];
    const done = list.filter(completed);
    const nb = list.length;
    const gross = done.reduce((memo, t) => memo + t.gross_amount, 0.00).toFixed(2);
    const fees = done.reduce(
        (memo, t) => memo + t.payment_system_fees + t.acquirer_fees,
        0.00
    ).toFixed(2);
    const net = done.reduce((memo, t) => memo + t.net_amount, 0.00).toFixed(2);
    return { nb, gross, fees, net };
}

const TransactionsResume = ({ transactions, askPaymentButton }) => {
    const { t }                      = useTranslation();
    const { user }                   = useSelector(state => state.auth);
    const  [isVisible, setIsvisible] = useState(true);
    const [resumeDetailsOpen, setResumeDetailsOpen] = useState(false);
    const [printForcedOpen, setPrintForcedOpen] = useState(false);

    useEffect(() => {
        const onBeforePrint = () => setPrintForcedOpen(true);
        const onAfterPrint = () => setPrintForcedOpen(false);
        window.addEventListener('beforeprint', onBeforePrint);
        window.addEventListener('afterprint', onAfterPrint);
        return () => {
            window.removeEventListener('beforeprint', onBeforePrint);
            window.removeEventListener('afterprint', onAfterPrint);
        };
    }, []);

    const showResumeDetails = resumeDetailsOpen || printForcedOpen;

    const resume = useMemo(() => {
        if (!transactions || !transactions.length)
            return null;

        const cur  = transactions[0].currency;
        const list = transactions;

        const anyCustomContract = list.some(
            tx => tx.custom_acq_contract || tx.custom_psp_contract
        );

        const profit_total = list.reduce(
            (memo, tx) => memo + (tx.netox_profit || 0),
            0.00
        ).toFixed(2);
        const due_total = list.filter(tx => !tx.payment_id).reduce(
            (memo, tx) => memo + tx.net_amount,
            0.00
        ).toFixed(2);

        if (!anyCustomContract) {
            const done = list.filter(completed);
            const nbTx = list.length;
            const effective_total = done.reduce(
                (memo, tx) => memo + tx.effective_amount,
                0.00
            ).toFixed(2);
            const fees_total = done.reduce(
                (memo, tx) => memo + tx.payment_system_fees + tx.acquirer_fees,
                0.00
            ).toFixed(2);
            const net_total = done.reduce(
                (memo, tx) => memo + tx.net_amount,
                0.00
            ).toFixed(2);
            return {
                cur,
                nbTx,
                anyCustomContract: false,
                net_total,
                profit_total,
                due_total,
                effective_total,
                fees_total
            };
        }

        const proxypayTxs = list.filter(noCustom);
        const px = aggregateFourMetrics(proxypayTxs);
        const customRows = buildCustomPartnerRows(list).map((row) => ({
            ...row,
            ...aggregateFourMetrics(row.txs)
        }));

        const doneAll = list.filter(completed);
        const globalSummary = {
            nb: list.length,
            effective_total: doneAll
                .reduce((memo, tx) => memo + tx.effective_amount, 0.00)
                .toFixed(2),
            net_total: doneAll
                .reduce((memo, tx) => memo + tx.net_amount, 0.00)
                .toFixed(2)
        };

        return {
            cur,
            anyCustomContract: true,
            profit_total,
            due_total,
            showProxypayRow: proxypayTxs.length > 0,
            proxypay: px,
            customRows,
            globalSummary
        };
    }, [transactions]);

    if (!resume)
        return "";

    const dueTotalValue = Number(resume.due_total) || 0;
    const canAskForPayment = user.isAdmin()
        ? dueTotalValue !== 0
        : dueTotalValue > 100;

    const printClass = user.isSuperAdmin() ? "sixColPrint colPrint" : "fiveColPrint colPrint";
    const lg_size    = 3;
    const curSuf     = ' ' + resume.cur;

    if (!resume.anyCustomContract) {
        return (
            <div className="TransactionsResume">
                <Row>
                    <Col sm={6} lg={lg_size} className={`${printClass}`}>
                        <Widget
                            title={ t('transactions.resume.nb_transactions') }
                            value={ resume.nbTx }
                        />
                    </Col>
                    <Col sm={6} lg={lg_size} className={`${printClass}`}>
                        <Widget
                            title={ t('transactions.resume.gross_total') }
                            value={ resume.effective_total + curSuf }
                        />
                    </Col>
                    <Col sm={6} lg={lg_size} className={`${printClass}`}>
                        <Widget
                            title={ t('transactions.resume.fees_total') }
                            value={ resume.fees_total + curSuf }
                        />
                    </Col>
                    <Col sm={6} lg={lg_size} className={`${printClass}`}>
                        <Widget
                            title={ t('transactions.resume.net_total') }
                            value={ resume.net_total + curSuf }
                        />
                    </Col>
                    <Col className={`${printClass}`}>
                        <Widget
                            title={ t('transactions.resume.due_total') }
                            value={ resume.due_total + curSuf }
                            action={ canAskForPayment ? askPaymentButton : null }
                        />
                    </Col>
                    { user.isSuperAdmin() && isVisible && (
                    <Col sm={6} lg={lg_size} className={`${printClass}`} onDoubleClick={() => setIsvisible(false)} >
                        <Widget
                            title={ t('transactions.resume.profit_total') }
                            value={ resume.profit_total + curSuf }
                        />
                    </Col>
                    ) }
                </Row>
            </div>
        );
    }

    const px = resume.proxypay;

    const summaryCol = { sm: 6, md: 4 };

    return (
        <div className="TransactionsResume">
            <div className="TransactionsResume-sectionHeading">
                <div className="TransactionsResume-sectionTitle">
                    { t('transactions.resume.section_summary_title') }
                </div>
                <div className="TransactionsResume-sectionSubtitle">
                    { t('transactions.resume.section_summary_subtitle') }
                </div>
            </div>
            <Row className="mb-2">
                <Col sm={summaryCol.sm} md={summaryCol.md} className={ printClass }>
                    <Widget
                        title={ t('transactions.resume.nb_transactions') }
                        value={ resume.globalSummary.nb }
                    />
                </Col>
                <Col sm={summaryCol.sm} md={summaryCol.md} className={ printClass }>
                    <Widget
                        title={ t('transactions.resume.gross_total') }
                        value={ resume.globalSummary.effective_total + curSuf }
                    />
                </Col>
                <Col sm={summaryCol.sm} md={summaryCol.md} className={ printClass }>
                    <Widget
                        title={ t('transactions.resume.net_total') }
                        value={ resume.globalSummary.net_total + curSuf }
                    />
                </Col>
            </Row>

            <div className="TransactionsResume-detailsToggle noPrint mb-2">
                <Button
                    color="link"
                    className="TransactionsResume-detailsToggleBtn p-0"
                    onClick={ () => setResumeDetailsOpen(o => !o) }
                >
                    <span className="TransactionsResume-detailsToggleInner">
                        <Icon
                            path={ showResumeDetails ? mdiChevronUp : mdiChevronDown }
                            size={ 0.8 }
                            color="currentColor"
                            className="TransactionsResume-detailsToggleIcon"
                        />
                        { showResumeDetails
                            ? t('transactions.resume.hide_details')
                            : t('transactions.resume.show_details') }
                    </span>
                </Button>
            </div>

            { showResumeDetails && (
            <div className="TransactionsResume-details">

            { resume.showProxypayRow && (
            <>
                <div className="TransactionsResume-sectionHeading TransactionsResume-sectionHeading--spaced">
                    <div className="TransactionsResume-sectionTitle">
                        { t('transactions.resume.section_proxypay') }
                    </div>
                    <div className="TransactionsResume-sectionSubtitle">
                        { t('transactions.resume.section_proxypay_subtitle') }
                    </div>
                </div>
                <Row className="mb-2">
                    <Col sm={6} lg={lg_size} className={`${printClass}`}>
                        <Widget
                            title={ t('transactions.resume.nb_transactions_proxypay') }
                            value={ px.nb }
                        />
                    </Col>
                    <Col sm={6} lg={lg_size} className={`${printClass}`}>
                        <Widget
                            title={ t('transactions.resume.gross_total_proxypay') }
                            value={ px.gross + curSuf }
                        />
                    </Col>
                    <Col sm={6} lg={lg_size} className={`${printClass}`}>
                        <Widget
                            title={ t('transactions.resume.fees_total_proxypay') }
                            value={ px.fees + curSuf }
                        />
                    </Col>
                    <Col sm={6} lg={lg_size} className={`${printClass}`}>
                        <Widget
                            title={ t('transactions.resume.net_total_proxypay') }
                            value={ px.net + curSuf }
                        />
                    </Col>
                </Row>
            </>
            ) }

            { resume.customRows.length > 0 && (
            <>
                <div className="TransactionsResume-sectionHeading TransactionsResume-sectionHeading--spaced">
                    <div className="TransactionsResume-sectionTitle">
                        { t('transactions.resume.section_partner_contracts') }
                    </div>
                    <div className="TransactionsResume-sectionSubtitle">
                        { t('transactions.resume.section_partner_contracts_subtitle') }
                    </div>
                </div>
            { resume.customRows.map((row) => (
                <Row key={ row.key } className="mb-2">
                    <Col sm={6} lg={lg_size} className={`${printClass}`}>
                        <Widget
                            title={ t('transactions.resume.nb_transactions_partner', {
                                partner: row.partner
                            }) }
                            value={ row.nb }
                        />
                    </Col>
                    <Col sm={6} lg={lg_size} className={`${printClass}`}>
                        <Widget
                            title={ t('transactions.resume.gross_total_partner', {
                                partner: row.partner
                            }) }
                            value={ row.gross + curSuf }
                        />
                    </Col>
                    <Col sm={6} lg={lg_size} className={`${printClass}`}>
                        <Widget
                            title={ t('transactions.resume.fees_total_partner', {
                                partner: row.partner
                            }) }
                            value={ row.fees + curSuf }
                        />
                    </Col>
                    <Col sm={6} lg={lg_size} className={ `${printClass} TransactionsResume-netUnavailable` }>
                        <Widget
                            title={ t('transactions.resume.net_total') }
                            value={ t('transactions.resume.net_total_contract_dependent') }
                        />
                    </Col>
                </Row>
            )) }
            </>
            ) }

            </div>
            ) }

            <Row>
                <Col className={`${printClass}`}>
                    <Widget
                        title={ t('transactions.resume.due_total') }
                        value={ resume.due_total + curSuf }
                        action={ canAskForPayment ? askPaymentButton : null }
                    />
                </Col>
                { user.isSuperAdmin() && isVisible && (
                <Col sm={6} lg={lg_size} className={`${printClass}`} onDoubleClick={() => setIsvisible(false)} >
                    <Widget
                        title={ t('transactions.resume.profit_total') }
                        value={ resume.profit_total + curSuf }
                    />
                </Col>
                ) }
            </Row>
        </div>
    );
};

export default TransactionsResume;
